import React, { FunctionComponent, useCallback, useEffect, useRef } from 'react';

import type { ClientApi } from '@yandex-int/messenger.sdk';
import type { WidgetEvent } from '@yandex-int/messenger.widget/lib/libs/WidgetEvent/WidgetEvent';
import cn from 'classnames';
import { connect, ConnectedProps, useSelector } from 'react-redux';

import { RootState } from '../../../../../../../init-store';
import Button from '../../../../../../shared/components/button';
import Spin from '../../../../../../shared/components/spin';
import SvgImage from '../../../../../../shared/components/svg-image';
import { selectIsMeetingRoomIntegration } from '../../../../../../shared/features/config/selectors';
import { showChat } from '../../../../../../shared/helpers';
import { countMetrika } from '../../../../../../shared/helpers/metrika';
import { useChatWidget } from '../../../../../../shared/hooks/messenger';
import {
    getChatId,
    getChatWidgetVisible,
    getConferenceStatus,
    getRoomId,
    isChatAllowedInConference,
    isConferenceWithBroadcast,
} from '../../../../selectors';
import { setSidebarContent } from '../../../sidebar/slice';
import {
    getChatAuthUserChanged,
    getChatUserGuid,
    getIsChatWidgetReady,
    setChatAuthUserChanged,
    setChatUnreadCount,
    setChatUserGuid,
    setIsChatWidgetReady,
    setOnlineInChatAction,
} from '../../slice';
import classes from './index.css';

const closeIcon = <SvgImage imageName="close2" />;

const mapState = (state: RootState) => {
    return {
        chatWidgetVisible: getChatWidgetVisible(state),
        chatId: getChatId(state),
        isChatAllowed: isChatAllowedInConference(state),
        conferenceStatus: getConferenceStatus(state),
        chatUserGuid: getChatUserGuid(state),
        roomId: getRoomId(state),
        authUserChanged: getChatAuthUserChanged(state),
        isChatWidgetReady: getIsChatWidgetReady(state),
        isConferenceWithBroadcast: isConferenceWithBroadcast(state),
    };
};

const mapDispatchToProps = {
    setChatUnreadCount,
    setSidebarContent,
    setOnlineInChat: setOnlineInChatAction,
    setChatAuthUserChanged,
    setIsChatWidgetReady,
    setChatUserGuid,
};

const connector = connect(mapState, mapDispatchToProps);

type ChatWidgetProps = ConnectedProps<typeof connector> & { visible: boolean };

const ChatWidget: FunctionComponent<ChatWidgetProps> = ({
    chatWidgetVisible,
    chatId,
    conferenceStatus,
    setChatUnreadCount,
    setSidebarContent,
    setOnlineInChat,
    setChatAuthUserChanged,
    authUserChanged,
    setIsChatWidgetReady,
    chatUserGuid,
    setChatUserGuid,
    isChatWidgetReady,
    isConferenceWithBroadcast,
    visible,
}) => {
    const chatRef = useRef<HTMLDivElement>(null);
    const isMeetingRoomIntegration = useSelector(selectIsMeetingRoomIntegration);

    const onChatCounter = useCallback(
        (event: WidgetEvent<ClientApi.Counter>) => {
            const value = event?.data?.valueForChat;
            /**
             * Выставляем счётчик непрочитанных сообщений на кнопку
             * Явно проверяем на null и undefined, т.к. значением может быть 0
             */
            if (value !== null && value !== undefined) {
                setChatUnreadCount(value);
            }
        },
        [setChatUnreadCount]
    );

    const onChatWidgetReady = useCallback(() => {
        setIsChatWidgetReady(true);
    }, [setIsChatWidgetReady]);

    const onChatUserLoaded = useCallback(
        (event: WidgetEvent<ClientApi.UserLoaded>) => {
            const { guid } = event.data ?? {};
            if (guid && chatUserGuid !== guid) {
                /**
                 * После авторизации новый guid не совпадает с гостевым (guid сохраняется в store) -
                 * надо юзера заново добавить в чат по новому guid
                 */
                setChatUserGuid(guid);
                if (authUserChanged) {
                    setOnlineInChat();
                }
            }
        },
        [chatUserGuid, setChatUserGuid, setOnlineInChat, authUserChanged]
    );

    const onChatUnload = useCallback(() => {
        /**
         * При авторизации виджет перезагружается
         */
        setChatAuthUserChanged(true);
        setIsChatWidgetReady(false);
    }, [setChatAuthUserChanged, setIsChatWidgetReady]);

    useChatWidget({
        chatId,
        mountNode: chatRef.current,
        ready: onChatWidgetReady,
        unload: onChatUnload,
        userLoaded: onChatUserLoaded,
        counter: onChatCounter,
    });

    useEffect(() => {
        if (!chatId) {
            return;
        }

        /**
         * Открываем чат только если виджет готов и виден.
         */
        if (isChatWidgetReady && chatWidgetVisible) {
            showChat({ chatId });
        }
    }, [isChatWidgetReady, chatWidgetVisible, conferenceStatus, chatId]);

    const onChatCloseControlClick = () => {
        countMetrika('meeting_screen', 'chat', isConferenceWithBroadcast ? 'stream_chat_close' : 'close');
        setSidebarContent(null);
    };

    return (
        <div className={cn(classes.chatBlock, { [classes.visible]: visible })} ref={chatRef}>
            {isMeetingRoomIntegration ? null : (
                <Button
                    size="sm"
                    view="neutral-transparent"
                    className={cn(classes.chatClose, { [classes.visible]: chatWidgetVisible })}
                    onClick={onChatCloseControlClick}
                    icon={closeIcon}
                />
            )}
            {!isChatWidgetReady ? (
                <div className={classes.spinWrapper}>
                    <Spin className={classes.spin} progress size="xl" color="cyan" />
                </div>
            ) : null}
        </div>
    );
};

export default connector(ChatWidget);
